/** Course catalog — levels of the German ladder with enroll + mastery. */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import * as api from "../../api/learning";
import { useAuth } from "../../auth/AuthContext";
import { Badge, Button, Card, EmptyState, ProgressRing, Spinner } from "../../components/ui";
import { useT } from "../../i18n";

export function CoursesPage() {
  const t = useT();
  // Only the account owner can buy; a school pupil is told who to ask.
  const { user } = useAuth();
  const canOrder = user?.role === "tenant_admin";
  const [courses, setCourses] = useState<api.CourseInfo[] | null>(null);
  const [progress, setProgress] = useState<Map<string, api.CourseProgress>>(new Map());

  useEffect(() => {
    let cancelled = false;
    Promise.all([api.listCourses(), api.myProgress()])
      .then(([courseRows, progressRows]) => {
        if (cancelled) return;
        setCourses(courseRows);
        setProgress(new Map(progressRows.map((p) => [p.course_id, p])));
      })
      .catch(() => {
        if (!cancelled) setCourses([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  if (courses === null) {
    return (
      <div className="grid place-items-center py-12">
        <Spinner size={28} />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-extrabold">{t("learn.title")}</h1>
        <p className="mt-1 text-muted">{t("learn.subtitle")}</p>
      </div>
      {courses.length === 0 ? (
        <Card>
          <EmptyState message={t("learn.empty")} />
        </Card>
      ) : (
        <div className="grid gap-4 md:grid-cols-2">
          {courses.map((course) => {
            const p = progress.get(course.id);
            // A level this account can't open offers a lock, never an Enroll
            // button that would only be refused.
            const locked = course.unlocked === false;
            return (
              <Card key={course.id} className={locked ? "opacity-75" : undefined}>
                <div className="flex items-center justify-between gap-4">
                  <div>
                    <Badge tone="brand">{course.level_code}</Badge>
                    <h2 className="mt-2 text-lg font-bold text-ink">{course.title}</h2>
                    {p ? (
                      <p className="mt-1 text-sm text-muted">
                        {p.lessons_completed}/{p.lessons_total} {t("learn.lessons")} ·{" "}
                        {p.xp_earned} {t("learn.xp")}
                      </p>
                    ) : (
                      course.description && (
                        <p className="mt-1 text-sm text-muted">{course.description}</p>
                      )
                    )}
                    {locked ? (
                      <p className="mt-3 text-sm font-semibold text-bronze-dark">
                        <span aria-hidden>🔒 </span>
                        {canOrder ? (
                          <Link
                            to={`/store?rec=pack.${course.level_code.split(".")[0].toLowerCase()}.learn`}
                            className="underline hover:text-brand"
                          >
                            {t("learn.lockedBuy")}
                          </Link>
                        ) : (
                          t("learn.lockedAsk")
                        )}
                      </p>
                    ) : (
                      <Link to={`/learn/courses/${course.id}`} className="mt-3 inline-block">
                        <Button variant={p ? "primary" : "secondary"}>
                          {p ? t("learn.resume") : t("learn.enroll")}
                        </Button>
                      </Link>
                    )}
                  </div>
                  {p && (
                    <ProgressRing
                      percent={p.mastery_percent}
                      size={72}
                      label={`${course.level_code} ${t("learn.mastery")}`}
                    />
                  )}
                </div>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
